import React, { useState, useEffect, useContext } from 'react';
import moment from 'moment';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  AlertTitle,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { ThemeContext } from 'src/store/ContextStore';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { gailRaceText } from './RiskRecordsList';

const REQUIRED_INPUTS = [
  ['age', 'Current age'],
  ['race', 'Race/Ethnicity'],
  ['ageMen', 'Age of first menstruation'],
  ['nBiops', 'Number of breast biopsies'],
  ['ageFLB', 'Age at first birth'],
  ['numRel', '1st degree relatives with breast cancer']
];

/**
 * Calculate Gail Score modal (sidebar "Risk Scores" tab). On open it fetches the
 * Gail inputs already saved on the member's Risk Factor record — the form is NOT
 * shown here; the fetched data is displayed read-only and used for calculation.
 */
export default function GailScoreModal({
  open,
  onClose,
  data,
  familyId,
  onCalculated
}) {
  const t = useAutoTranslation();

  const memberId = data?.proBandId ?? data?.familyMemberId;
  const userId = data?.user_id;
  const { theme } = useContext(ThemeContext);
  const dateFormat = theme?.dateFormat || 'DD/MM/YYYY';

  const [inputs, setInputs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [score, setScore] = useState(null);
  const [error, setError] = useState('');

  // Fetch the saved Gail inputs when the modal opens. user_id is required by the
  // endpoint — it is what the ownership check authorises against.
  useEffect(() => {
    if (!open || !memberId || !userId) return;
    setScore(null);
    setError('');
    setLoading(true);
    axios
      .get(
        `${APICONSTANTS.getRiskFactor.replace(
          ':pid',
          memberId
        )}?recordType=gail&user_id=${userId}`
      )
      .then(({ data: rec }) => setInputs(rec || null))
      .catch(() => setInputs(null))
      .finally(() => setLoading(false));
  }, [open, memberId, userId]);

  const rows = inputs
    ? [
        ['Current age', inputs.age],
        [
          'Race/Ethnicity',
          inputs.race != null && inputs.race !== ''
            ? gailRaceText(inputs.race, t)
            : inputs.race
        ],
        ['Age of first menstruation', inputs.ageMen],
        ['Number of breast biopsies', inputs.nBiops],
        ['Age at first birth', inputs.ageFLB],
        ['1st degree relatives with breast cancer', inputs.numRel],
        [
          'Personal history of breast cancer / DCIS / LCIS',
          inputs.isBreastCancer ? t('Yes') : t('No')
        ],
        ['Known BRCA1/BRCA2 mutation', inputs.isMutation ? t('Yes') : t('No')]
      ].filter(([, v]) => v != null && v !== '')
    : [];

  const handleCalculate = () => {
    if (!inputs) return;
    setError('');
    if (inputs.isBreastCancer || inputs.isMutation) {
      return setError(
        t(
          'Gail cannot be calculated for a personal history of breast cancer / DCIS / LCIS or a known BRCA1/BRCA2 mutation.'
        )
      );
    }
    if (!(inputs.age >= 35 && inputs.age <= 85)) {
      return setError(
        t('Gail calculates risk for ages 35–85. Update the saved inputs.')
      );
    }
    const missing = REQUIRED_INPUTS.filter(
      ([key]) => inputs[key] == null || inputs[key] === ''
    ).map(([, label]) => t(label));
    if (missing.length) {
      return setError(
        missing.length > 1
          ? t(
              'Missing inputs: {{inputs}}. Add them in Clinical Records → Risk Factor, then calculate again.',
              { inputs: missing.join(', ') }
            )
          : t(
              'Missing input: {{inputs}}. Add it in Clinical Records → Risk Factor, then calculate again.',
              { inputs: missing.join(', ') }
            )
      );
    }

    setCalculating(true);
    axios
      .post(APICONSTANTS.calculateGailRiskScore, {
        recordId: inputs._id,
        isBreastCancer: inputs.isBreastCancer,
        isMutation: inputs.isMutation,
        age: Number(inputs.age),
        laterAge: Number(inputs.age) + 5,
        ageMen: Number(inputs.ageMen),
        nBiops: Number(inputs.nBiops),
        ageFLB: Number(inputs.ageFLB),
        numRel: Number(inputs.numRel),
        race: inputs.race,
        pid: data.pid,
        user_id: data.user_id,
        familyId,
        familyMemberId: memberId
      })
      .then((resp) => {
        setScore(resp.data?.riskScore ?? null);
        onCalculated?.();
      })
      .catch((err) => {
        // Surface whatever the backend NAMED, not a generic apology.
        //
        // Two kinds of response carry a usable message. The 503 for a missing
        // host dependency (R) carries `code: 'R_NOT_AVAILABLE'`, so that "the
        // server is missing the risk engine" is not mistaken for "my inputs are
        // wrong". And since TGCLOUD-1748 the route also validates the six
        // numeric inputs and race, answering 400 with the field that is missing
        // ("Cannot calculate a Gail score — missing input: Age at first
        // birth."). Only the R_NOT_AVAILABLE code was being matched, so that
        // 400 — the one response that tells the clinician exactly what to go
        // and fill in — was thrown away and replaced with "An error has
        // occurred!".
        //
        // Any 4xx is by definition about the request, so its message is
        // addressed to this user and safe to show. A 5xx is not, and keeps the
        // generic wording unless it identifies itself the way the R probe does.
        const body = err?.response?.data;
        const status = err?.response?.status;
        const serverMessage =
          typeof body?.message === 'string' ? body.message.trim() : '';
        const isClientError = status >= 400 && status < 500;
        setError(
          serverMessage && (isClientError || body?.code === 'R_NOT_AVAILABLE')
            ? serverMessage
            : t('An error has occurred!')
        );
      })
      .finally(() => setCalculating(false));
  };

  // No saved inputs → open the Clinical Records "Risk Factor" modal (Gail) so
  // the user can create them. ClinicalNotes listens for this event.
  const openInClinicalRecords = () => {
    window.dispatchEvent(
      new CustomEvent('open-risk-factor-modal', { detail: { model: 'gail' } })
    );
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        {t('Calculate Gail Score')}
        {/* The title bar is painted (Dashboard's DialogTitle override), so the
            close takes the bar's text colour, as every other dialog's does;
            MUI's default drew it black on the navy. */}
        <IconButton
          onClick={onClose}
          size="small"
          aria-label={t('close')}
          sx={{ color: 'inherit' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary'
            }}
          >
            {t('Loading saved inputs…')}
          </Typography>
        ) : !inputs ? (
          <Alert severity="warning">
            {t(
              'Missing data — no Gail inputs have been saved for this individual. Create them in Clinical Records → Risk Factor first.'
            )}
          </Alert>
        ) : (
          <Alert severity="info" icon={false}>
            <AlertTitle
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 1
              }}
            >
              <span>{t('Data used for calculation')}</span>
              {(inputs.updatedAt || inputs.createdAt) && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={t('Captured {{value}}', {
                    value: moment(inputs.updatedAt || inputs.createdAt).format(
                      dateFormat
                    )
                  })}
                  sx={{ fontWeight: 400 }}
                />
              )}
            </AlertTitle>
            {rows.map(([label, value]) => (
              <Box
                key={label}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  py: 0.25
                }}
              >
                <Typography variant="body2" sx={{ mr: 2, opacity: 0.85 }}>
                  {t(label)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600
                  }}
                >
                  {String(value)}
                </Typography>
              </Box>
            ))}
          </Alert>
        )}

        {score != null && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('GAIL RISK SCORE -')} {score}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t('Close')}
        </Button>
        {inputs ? (
          <Button
            variant="contained"
            color="secondary"
            onClick={handleCalculate}
            disabled={calculating}
          >
            {t('Calculate')}
          </Button>
        ) : (
          !loading && (
            <Button
              variant="contained"
              color="secondary"
              onClick={openInClinicalRecords}
            >
              {t('Add in Clinical Records')}
            </Button>
          )
        )}
      </DialogActions>
    </Dialog>
  );
}
